import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inventoryAPI, propertyAPI, leaseAPI, propertyUnitAPI } from '../../services/api';
import SignaturePad from '../../components/SignaturePad';
import { toast } from 'react-toastify';

const ROOMS_PRESETS = ['Salon', 'Cuisine', 'Chambre 1', 'Chambre 2', 'Chambre 3', 'Salle de bain', 'WC', 'Couloir', 'Balcon', 'Cave', 'Garage'];
const ITEMS_PRESETS = {
  Salon: ['Murs', 'Plafond', 'Sol', 'Fenêtres', 'Portes', 'Prises électriques', 'Luminaires', 'Volets'],
  Cuisine: ['Murs', 'Plafond', 'Sol', 'Évier', 'Placard', 'Réfrigérateur', 'Cuisinière', 'Hotte', 'Lave-vaisselle', 'Robinetterie'],
  'Chambre 1': ['Murs', 'Plafond', 'Sol', 'Fenêtres', 'Portes', 'Placards', 'Prises électriques'],
  'Salle de bain': ['Murs', 'Plafond', 'Sol', 'Baignoire/Douche', 'Lavabo', 'WC', 'Robinetterie', 'Miroir'],
};

const CONDITIONS = [
  { value: 'new', label: 'Neuf', color: '#16a34a', bg: '#dcfce7' },
  { value: 'good', label: 'Bon état', color: '#2563eb', bg: '#dbeafe' },
  { value: 'fair', label: 'Passable', color: '#ca8a04', bg: '#fef9c3' },
  { value: 'poor', label: 'Mauvais', color: '#ea580c', bg: '#ffedd5' },
  { value: 'damaged', label: 'Dégradé', color: '#dc2626', bg: '#fee2e2' },
];

const InventoryBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [formData, setFormData] = useState({
    property_id: '',
    property_unit_id: '',
    lease_contract_id: '',
    type: 'check-in',
    notes: '',
    conducted_date: new Date().toISOString().split('T')[0],
  });
  const [rooms, setRooms] = useState([]); // [{name, items:[{item_name, condition, description, repair_cost_estimate}]}]
  const [properties, setProperties] = useState([]);
  const [leases, setLeases] = useState([]);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [tenantSig, setTenantSig] = useState('');
  const [agentSig, setAgentSig] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeRoom, setActiveRoom] = useState(0);
  const [newRoomName, setNewRoomName] = useState('');
  const [showRoomInput, setShowRoomInput] = useState(false);

  const fetchProperties = useCallback(async () => {
    try {
      const res = await propertyAPI.list({ limit: 100 });
      const data = res.data.data || res.data;
      setProperties(Array.isArray(data) ? data : []);
    } catch {
      setProperties([]);
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    if (!id) return;
    try {
      const res = await inventoryAPI.getById(id);
      const data = res.data;
      setFormData({
        property_id: data.property_id || '',
        property_unit_id: data.property_unit_id || '',
        lease_contract_id: data.lease_contract_id || '',
        type: data.type || 'check-in',
        notes: data.notes || '',
        conducted_date: data.conducted_date ? data.conducted_date.split('T')[0] : new Date().toISOString().split('T')[0],
      });
      // Load units if property is multi-unit
      if (data.property_id) {
        const pRes = await propertyAPI.list({ limit: 100 });
        const allProps = pRes.data.data || pRes.data;
        const propList = Array.isArray(allProps) ? allProps : [];
        setProperties(propList);
        const prop = propList.find(p => String(p.id) === String(data.property_id));
        setSelectedProperty(prop || null);
        if (prop?.is_multi_unit) {
          propertyUnitAPI.listByProperty(data.property_id)
            .then(r => setUnits(r.data || []))
            .catch(() => setUnits([]));
        }
      }
      setTenantSig(data.tenant_signature_url || '');
      setAgentSig(data.owner_agent_signature_url || '');
      // Reconstruct rooms from flat items
      const roomMap = {};
      (data.items || []).forEach(item => {
        if (!roomMap[item.room_name]) roomMap[item.room_name] = [];
        roomMap[item.room_name].push(item);
      });
      setRooms(Object.entries(roomMap).map(([name, items]) => ({ name, items })));
    } catch {
      toast.error('Erreur lors du chargement de l\'état des lieux');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProperties();
    if (!isNew) {
      fetchInventory();
    }
  }, [isNew, fetchProperties, fetchInventory]);

  // Fetch leases when property changes
  useEffect(() => {
    if (formData.property_id) {
      leaseAPI.list({ property_id: formData.property_id, limit: 100 })
        .then(res => {
          const data = res.data.data || res.data;
          setLeases(Array.isArray(data) ? data : []);
        })
        .catch(() => setLeases([]));
    } else {
      setLeases([]);
    }
  }, [formData.property_id]);

  // Handle property change for units
  const handlePropertyChange = async (propertyId) => {
    const prop = properties.find(p => String(p.id) === String(propertyId));
    setSelectedProperty(prop || null);
    setFormData(f => ({ ...f, property_id: propertyId, property_unit_id: '', lease_contract_id: '' }));
    setUnits([]);
    if (prop?.is_multi_unit && propertyId) {
      setLoadingUnits(true);
      try {
        const r = await propertyUnitAPI.listByProperty(propertyId);
        setUnits(r.data || []);
      } catch (e) {
        setUnits([]);
      } finally {
        setLoadingUnits(false);
      }
    }
  };

  const handleSave = async () => {
    if (!formData.property_id) return toast.warn('Veuillez sélectionner un bien immobilier');
    setSaving(true);
    try {
      const payload = {
        ...formData,
        lease_contract_id: formData.lease_contract_id || null,
        property_unit_id: formData.property_unit_id || null,
      };
      if (isNew) {
        const res = await inventoryAPI.create(payload);
        toast.success('État des lieux créé avec succès !');
        navigate(`/inventories/${res.data.id}`);
      } else {
        const flatItems = rooms.flatMap(r => r.items.map(it => ({ ...it, room_name: r.name })));
        await Promise.all([
          inventoryAPI.update(id, {
            ...payload,
            tenant_signature_url: tenantSig,
            owner_agent_signature_url: agentSig,
            status: (tenantSig && agentSig) ? 'signed' : 'draft',
          }),
          inventoryAPI.updateItems(id, { items: flatItems }),
        ]);
        toast.success('État des lieux sauvegardé !');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      const res = await inventoryAPI.downloadPDF(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `etat-des-lieux-${formData.type}-${id?.substring(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || 'Erreur lors du téléchargement du PDF');
    }
  };

  // ── Room management ──
  const addRoom = (name) => {
    const n = name.trim();
    if (!n || rooms.find(r => r.name === n)) return;
    const presets = ITEMS_PRESETS[n] || [];
    const newRoom = { name: n, items: presets.map(it => ({ item_name: it, condition: 'good', description: '', repair_cost_estimate: 0 })) };
    setRooms([...rooms, newRoom]);
    setActiveRoom(rooms.length);
    setShowRoomInput(false);
    setNewRoomName('');
  };

  const removeRoom = (idx) => {
    setRooms(rooms.filter((_, i) => i !== idx));
    setActiveRoom(Math.max(0, activeRoom - 1));
  };

  // ── Item management ──
  const addItem = (roomIdx) => {
    const updated = [...rooms];
    updated[roomIdx].items.push({ item_name: '', condition: 'good', description: '', repair_cost_estimate: 0 });
    setRooms(updated);
  };

  const updateItem = (roomIdx, itemIdx, field, value) => {
    const updated = [...rooms];
    updated[roomIdx].items[itemIdx][field] = value;
    setRooms(updated);
  };

  const removeItem = (roomIdx, itemIdx) => {
    const updated = [...rooms];
    updated[roomIdx].items = updated[roomIdx].items.filter((_, i) => i !== itemIdx);
    setRooms(updated);
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  const currentRoom = rooms[activeRoom];

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/inventories')} style={{ marginRight: 12 }}>
            ← Retour
          </button>
          <span style={{ fontSize: 22, fontWeight: 700 }}>
            {isNew ? 'Nouvel état des lieux' : `État des lieux — ${formData.type === 'check-in' ? 'Entrée' : 'Sortie'}`}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isNew && (
            <button className="btn btn-secondary" onClick={handleDownloadPDF}>📄 Télécharger PDF</button>
          )}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sauvegarde...' : (isNew ? 'Créer et continuer →' : '💾 Sauvegarder')}
          </button>
        </div>
      </div>

      {/* ── Infos générales ── */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header"><h2>Informations générales</h2></div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div className="form-group">
              <label>Type *</label>
              <select className="form-control" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
                <option value="check-in">Entrée</option>
                <option value="check-out">Sortie</option>
              </select>
            </div>
            <div className="form-group">
              <label>Bien *</label>
              <select
                className="form-control"
                value={formData.property_id}
                onChange={e => handlePropertyChange(e.target.value)}
                required
              >
                <option value="">— Sélectionner un bien —</option>
                {properties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.title} {p.city ? `(${p.city})` : ''}{p.is_multi_unit ? ' 🏢' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Sélection d'unité si le bien est multi-unités */}
            {selectedProperty?.is_multi_unit && (
              <div className="form-group" style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px' }}>
                <label style={{ color: '#1d4ed8', fontWeight: 600, fontSize: 13 }}>🏢 Unité (optionnel)</label>
                {loadingUnits ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#64748b', fontSize: 12 }}>
                    <div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Chargement...
                  </div>
                ) : (
                  <select
                    className="form-control"
                    value={formData.property_unit_id}
                    onChange={e => setFormData({ ...formData, property_unit_id: e.target.value })}
                  >
                    <option value="">Bien entier (pas d'unité spécifique)</option>
                    {units.map(u => (
                      <option key={u.id} value={u.id}>
                        {u.unit_number} — {u.unit_type === 'apartment' ? 'Appt' : u.unit_type}{u.surface_area ? ` · ${u.surface_area}m²` : ''}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div className="form-group">
              <label>Contrat de bail (optionnel)</label>
              <select className="form-control" value={formData.lease_contract_id} onChange={e => setFormData({ ...formData, lease_contract_id: e.target.value })}>
                <option value="">— Aucun contrat —</option>
                {leases.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.contract_number} {l.tenant_first_name ? `(${l.tenant_first_name} ${l.tenant_last_name})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Date de visite *</label>
              <input type="date" className="form-control" value={formData.conducted_date} onChange={e => setFormData({ ...formData, conducted_date: e.target.value })} />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Notes générales</label>
              <textarea className="form-control" rows={2} value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Observations générales..." />
            </div>
          </div>
        </div>
      </div>

      {/* ── Pièces & Éléments (only when editing) ── */}
      {!isNew && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Pièces & Éléments</h2>
            <button className="btn btn-primary btn-sm" onClick={() => setShowRoomInput(true)}>+ Ajouter une pièce</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ display: 'flex', minHeight: 400 }}>
              {/* Room sidebar */}
              <div style={{ width: 200, borderRight: '1px solid var(--border)', flexShrink: 0 }}>
                {rooms.map((room, idx) => (
                  <div
                    key={idx}
                    onClick={() => setActiveRoom(idx)}
                    style={{
                      padding: '10px 16px', cursor: 'pointer', fontSize: 13,
                      background: idx === activeRoom ? 'var(--primary)' : 'transparent',
                      color: idx === activeRoom ? 'white' : 'inherit',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <span>{room.name}</span>
                    <span style={{ fontSize: 11, opacity: 0.7 }}>{room.items.length} élém.</span>
                  </div>
                ))}
                {/* Quick room presets */}
                {ROOMS_PRESETS.filter(r => !rooms.find(rm => rm.name === r)).slice(0, 5).map(r => (
                  <div
                    key={r}
                    onClick={() => addRoom(r)}
                    style={{ padding: '8px 16px', cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <span style={{ fontSize: 10 }}>+</span> {r}
                  </div>
                ))}
                {showRoomInput && (
                  <div style={{ padding: 10 }}>
                    <input
                      autoFocus
                      className="form-control"
                      placeholder="Nom de la pièce"
                      value={newRoomName}
                      onChange={e => setNewRoomName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addRoom(newRoomName); if (e.key === 'Escape') setShowRoomInput(false); }}
                      style={{ fontSize: 12, marginBottom: 4 }}
                    />
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }} onClick={() => addRoom(newRoomName)}>OK</button>
                  </div>
                )}
              </div>

              {/* Items panel */}
              <div style={{ flex: 1, padding: 20, overflowY: 'auto' }}>
                {!currentRoom ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                    <div style={{ fontSize: 40, marginBottom: 12 }}>🏠</div>
                    <p>Ajoutez une pièce pour commencer la saisie</p>
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                      <h3 style={{ margin: 0, fontSize: 16 }}>{currentRoom.name}</h3>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-primary btn-sm" onClick={() => addItem(activeRoom)}>+ Élément</button>
                        <button className="btn btn-danger btn-sm" onClick={() => removeRoom(activeRoom)}>Supprimer la pièce</button>
                      </div>
                    </div>

                    {/* Items table */}
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg)', borderBottom: '2px solid var(--border)' }}>
                          <th style={{ ...thStyle, width: '25%' }}>Élément / Équipement</th>
                          <th style={{ ...thStyle, width: '20%' }}>État</th>
                          <th style={{ ...thStyle, width: '35%' }}>Observations</th>
                          <th style={{ ...thStyle, width: '15%' }}>Coût réparation (XOF)</th>
                          <th style={{ ...thStyle, width: '5%' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentRoom.items.map((item, itemIdx) => (
                          <tr key={itemIdx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <input
                                className="form-control"
                                value={item.item_name}
                                onChange={e => updateItem(activeRoom, itemIdx, 'item_name', e.target.value)}
                                placeholder="Ex: Mur nord"
                                style={{ fontSize: 13 }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <select
                                className="form-control"
                                value={item.condition}
                                onChange={e => updateItem(activeRoom, itemIdx, 'condition', e.target.value)}
                                style={{
                                  fontSize: 12, fontWeight: 600,
                                  color: CONDITIONS.find(c => c.value === item.condition)?.color,
                                  background: CONDITIONS.find(c => c.value === item.condition)?.bg + '80',
                                }}
                              >
                                {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                              </select>
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input
                                className="form-control"
                                value={item.description}
                                onChange={e => updateItem(activeRoom, itemIdx, 'description', e.target.value)}
                                placeholder="Détails..."
                                style={{ fontSize: 13 }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              <input
                                type="number"
                                className="form-control"
                                value={item.repair_cost_estimate || ''}
                                onChange={e => updateItem(activeRoom, itemIdx, 'repair_cost_estimate', e.target.value)}
                                placeholder="0"
                                style={{ fontSize: 13 }}
                              />
                            </td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              <button
                                onClick={() => removeItem(activeRoom, itemIdx)}
                                style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#dc2626', fontSize: 16 }}
                              >×</button>
                            </td>
                          </tr>
                        ))}
                        {currentRoom.items.length === 0 && (
                          <tr>
                            <td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
                              Aucun élément — cliquez "+ Élément" pour ajouter
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Résumé état de la pièce */}
                    {currentRoom.items.length > 0 && (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {CONDITIONS.map(c => {
                          const count = currentRoom.items.filter(i => i.condition === c.value).length;
                          if (!count) return null;
                          return (
                            <span key={c.value} style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, background: c.bg, color: c.color, fontWeight: 600 }}>
                              {c.label} : {count}
                            </span>
                          );
                        })}
                        {currentRoom.items.some(i => i.repair_cost_estimate > 0) && (
                          <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, background: '#fee2e2', color: '#dc2626', fontWeight: 600 }}>
                            Réparations : {currentRoom.items.reduce((s, i) => s + parseFloat(i.repair_cost_estimate || 0), 0).toLocaleString()} XOF
                          </span>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Signatures ── */}
      {!isNew && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><h2>Signatures numériques</h2></div>
          <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 32 }}>
            {[
              { label: 'Signature du Locataire', value: tenantSig, setter: setTenantSig },
              { label: 'Signature Agent / Agence', value: agentSig, setter: setAgentSig },
            ].map(({ label, value, setter }) => (
              <div key={label}>
                <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>{label}</div>
                {value ? (
                  <div>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                      <img src={value} alt={label} style={{ width: '100%', maxHeight: 150, objectFit: 'contain', background: '#fff' }} />
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => setter('')}>↺ Re-signer</button>
                    <span style={{ marginLeft: 8, color: '#16a34a', fontSize: 12 }}>✓ Signé</span>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                      Signez dans le cadre ci-dessous (fonctionne au doigt sur tablette/téléphone)
                    </p>
                    <SignaturePad onSave={(data) => setter(data)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Récapitulatif ── */}
      {!isNew && rooms.length > 0 && (
        <div className="card" style={{ marginBottom: 20, background: 'var(--bg)' }}>
          <div className="card-body" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Récapitulatif</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                {rooms.length} pièce(s) · {rooms.reduce((s, r) => s + r.items.length, 0)} élément(s)
              </div>
            </div>
            {rooms.some(r => r.items.some(i => i.repair_cost_estimate > 0)) && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>Coût total estimé</div>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#dc2626' }}>
                  {rooms.reduce((s, r) => s + r.items.reduce((ss, i) => ss + parseFloat(i.repair_cost_estimate || 0), 0), 0).toLocaleString()} XOF
                </div>
              </div>
            )}
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Sauvegarde...' : '💾 Sauvegarder'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600,
  color: 'var(--text-secondary)', textTransform: 'uppercase',
};

export default InventoryBuilder;
